"use client";

import { cn } from "@/lib/utils";
import { Button } from "@/components/atoms/button";
import Link from "next/link";
import { SanityImage } from "@/components/atoms/SanityImage";
import { resolveInternalLink } from "@/lib/routing";
import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";
import { Heading } from "@/components/atoms/Heading";
import type { HeroProps } from "./Hero.props";

interface HeroSideImageContentProps {
  data: HeroProps["data"];
  isDark: boolean;
}

/**
 * Client component for HeroSideImage's animated inner content.
 * Kept separate so HeroSideImage can remain a server component (import "server-only").
 */
export const HeroSideImageContent = ({ data, isDark }: HeroSideImageContentProps) => {
  return (
    <Box className="flex flex-col items-center justify-between gap-16 lg:flex-row">
      {/* Text column */}
      <Box className="flex flex-col items-center gap-8 text-center lg:items-start lg:text-left lg:w-1/2">
        {data.title && (
          <Heading level={1} className="text-5xl font-bold tracking-tight text-pretty lg:text-7xl leading-condensed">
            <Text
              className={cn(
                "inline-block",
                isDark
                  ? "bg-clip-text text-transparent bg-gradient-to-r from-white to-white/60"
                  : "text-foreground",
              )}
            >
              {data.title}
            </Text>
            {data.titleLine2 && (
              <Text className="block text-primary mt-2 drop-shadow-sm">
                {` ${data.titleLine2}`}
              </Text>
            )}
          </Heading>
        )}

        {data.subtitle && (
          <Text as="p"
            className={cn(
              "max-w-xl text-xl font-medium lg:text-2xl leading-relaxed",
              isDark ? "text-white/70" : "text-foreground/70",
            )}
          >
            {data.subtitle}
          </Text>
        )}

        {data.ctas && data.ctas.length > 0 && (
          <Box className="flex w-full justify-center lg:justify-start gap-4">
            {data.ctas.map((cta, index) => {
              const action = cta.actions?.[0];
              const href =
                action?.linkType === "internal"
                  ? resolveInternalLink(action.internalLink)
                  : action?.href || "#";
              const isPrimary = index === 0;
              return (
                <Button
                  key={cta._key}
                  asChild
                  size="lg"
                  className={cn(
                    "h-14 px-8 rounded-xl font-bold text-lg shadow-xl transition-all duration-300 active:scale-95",
                    isPrimary
                      ? "bg-primary hover:bg-primary/90"
                      : "bg-white/10 backdrop-blur-md border-white/20 hover:bg-white/20",
                  )}
                >
                  <Link href={href}>{cta.heading || "Learn More"}</Link>
                </Button>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Image column */}
      {data.image && (
        <Box className="relative aspect-photo w-full lg:w-1/2">
          <Box className="relative h-full w-full overflow-hidden rounded-3xl border border-white/10 shadow-2xl">
            <SanityImage
              image={data.image}
              fill
              priority
              aspectRatio="4/3"
              sizes="(max-width: 1024px) 100vw, 50vw"
              className="object-cover"
            />
            <Box className="absolute inset-0 bg-gradient-to-tr from-aq-navy/20 to-transparent pointer-events-none" />
          </Box>
        </Box>
      )}
    </Box>
  );
};
